import { gql } from "@apollo/client";
import { ChannelConfig, MarketplaceConfig } from "TypesAndInterfaces";

export type CreateMarketplaceInput = {
  channels?: ChannelConfig[];
  config: MarketplaceConfig;
  name: string;
  disabled?: boolean;
};

export type UpdateMarketplaceInput = CreateMarketplaceInput & {
  id: number;
};
export interface PaginationInput {
  search: number;
  limit: number;
  offset: number;
}

export interface MarketplaceBlackLists {
  marketplaceId: number | undefined;
  shopPagination?: PaginationInput;
  invoicePagination?: PaginationInput;
}

export const isFeatureEnabledQuery = gql`
  query IsFeatureEnabled($flagName: FeatureFlagName!) {
    isFeatureEnabled(flagName: $flagName)
  }
`;

export const isFeatureEnabledForMarketplaceQuery = gql`
  query IsFeatureEnabledForMarketplace($marketplaceId: Float!, $flagName: FeatureFlagName!) {
    isFeatureEnabledForMarketplace(marketplaceId: $marketplaceId, flagName: $flagName)
  }
`;

export const createMarketplaceMutation = gql`
  mutation CreateMarketplace($data: CreateMarketplaceInput!) {
    createMarketplace(data: $data) {
      id
      name
      disabled
      config {
        account {
          hopReturnUrl
          marketplaceOperatorEmails
          manageBlockPaymentFlags
          noOfSellerQuestions
          unsuspendNewShops
          useSubscription
          useChannels
          useBalancePlatforms
          useCommision
          miraklSsoEnabled
          onlyIdReferenceForBeneficiary
          usePreBalancePlatforms
          invoiceSyncScheduler {
            description
            value
          }
          accountFullSyncScheduler {
            description
            value
          }
          accountPartialSyncScheduler {
            description
            value
          }
          maxInvoiceRetryCount
          locale
        }
        mirakl {
          url
          key
          ssoClientId
          ssoClientSecret
        }
      }
      channels {
        id
        code
        config{
          hopOptions{
            allowBankAccountFormatSelection,
            allowIntraRegionCrossBorderPayout
          }
          routePriorities
          enablePreLiableCommissions
        }
        isDefault
        marketplaceId
        balancePlatform
        preBalanceAccountId
        lemApi {
          url
          isBasicAuth
          user
          password
          key
        }
        balanceApi {
          url
          isBasicAuth
          user
          password
          key
        }
        balanceAccountId
        notificationUrl
        notificationSettings {
          configuration {
            user
            password
            hmac
          }
          transfer {
            user
            password
            hmac
          }
          transferInstrument {
            user
            password
            hmac
          }
          legalEntity {
            user
            password
            hmac
          }
        }
      }
    }
  }
`;

export const updateMarketplaceMutation = gql`
  mutation UpdateMarketplace($data: UpdateMarketplaceInput!) {
    updateMarketplace(data: $data) {
      id
      name
      disabled
      config {
        account {
          hopReturnUrl
          marketplaceOperatorEmails
          manageBlockPaymentFlags
          noOfSellerQuestions
          unsuspendNewShops
          useSubscription
          useChannels
          useBalancePlatforms
          useCommision
          miraklSsoEnabled
          onlyIdReferenceForBeneficiary
          invoiceSyncScheduler {
            description
            value
          }
          accountFullSyncScheduler {
            description
            value
          }
          accountPartialSyncScheduler {
            description
            value
          }
          maxInvoiceRetryCount
          locale
        }
        mirakl {
          url
          key
          ssoClientId
          ssoClientSecret
        }
        blacklists {
          shops
          invoices
        }
      }
      channels {
        id
        code
        config{
          hopOptions{
            allowBankAccountFormatSelection,
            allowIntraRegionCrossBorderPayout
          }
          routePriorities
          enablePreLiableCommissions
        }
        isDefault
        marketplaceId
        balancePlatform
        lemApi {
          url
          isBasicAuth
          user
          password
          key
        }
        balanceApi {
          url
          isBasicAuth
          user
          password
          key
        }
        balanceAccountId
        notificationUrl
        notificationSettings {
          configuration {
            user
            password
            hmac
          }
          transfer {
            user
            password
            hmac
          }
          transferInstrument {
            user
            password
            hmac
          }
          legalEntity {
            user
            password
            hmac
          }
        }
      }
    }
  }
`;

export const checkMarketplaceConfigQuery = gql`
  query checkMarketplaceConfig(
    $config: MarketplaceConfigInput!
    $channels: [ChannelConfigInput!]!
    $marketplaceId: Float
  ) {
    checkMarketplaceConfig(
      config: $config
      channels: $channels
      marketplaceId: $marketplaceId
    ) {
      checkMarketplaceConfig
      miraklPlatformConfigurations  
    }
  }
`;

export const startMarketplaceInvoiceSyncMutation = gql`
  mutation startMarketplaceInvoiceSync(
    $marketplaceId: Float!
    $startDate: DateTimeISO
  ) {
    startMarketplaceInvoiceSync(
      marketplaceId: $marketplaceId
      startDate: $startDate
    )
  }
`;

export const startMarketplaceAccountFullSyncMutation = gql`
  mutation startMarketplaceAccountFullSync($marketplaceId: Float!) {
    startMarketplaceAccountFullSync(marketplaceId: $marketplaceId)
  }
`;
export const startMarketplaceAccountPartialSyncMutation = gql`
  mutation startMarketplaceAccountPartialSync($marketplaceId: Float!) {
    startMarketplaceAccountPartialSync(marketplaceId: $marketplaceId)
  }
`;

export const deleteMarketplaceChannelMutation = gql`
  mutation deleteMarketplaceChannel($channelId: Float!) {
    deleteMarketplaceChannel(channelId: $channelId)
  }
`;

export const startFullSyncForSpecificChannelMutation = gql`
  mutation startFullSyncForSpecificChannel($marketplaceId: Float!, $channelId: Float!) {
    startFullSyncForSpecificChannel(marketplaceId: $marketplaceId, channelId: $channelId)
  }
`;

export const getMarketplaceBlacklistQuery = gql`
  query GetMarketplaceBlacklists($pagination: MarketplaceBlackLists!) {
    getMarketplaceBlacklists(pagination: $pagination) {
      invoices
      shops
    }
  }
`;

export const getMiraklPlatformConfig = gql`
  query getMiraklPlatformConfig($marketplaceId: Float!) {
    getMiraklPlatformConfig(marketplaceId: $marketplaceId) {
      features {
        kycStatusPerSeller
        paymentConfirmation
        shopTaxOptions {
          localTaxNumber {
            mandatory
          }
          taxIdentificationNumber {
            mandatory
          }
        }
      }
    }
  }
`;
